import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button, { ButtonColor, ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Loader } from 'src/components/atoms/Loader';
import Text, { TextAlign } from 'src/components/atoms/Text';
import PDFViewer from 'src/components/molecules/PDFViewer/PDFViewer';
import {
  AssetVerificationReviewProvider,
  hasStaleStartStatement,
  proofForDocument,
  reasonCodeLabel,
  voaStorageKey,
  useHydrateReviewState,
  validateDocument,
  staleExcludingCodesForDocument,
  VOA_REASON_CODE_STALE_START
} from 'src/features/assetVerification';
import { useProofsFilteredByEntryId } from 'src/features/proofs/hooks';
import useUserFeature from 'src/hooks/useUserFeature';
import { EmbedMode } from 'src/ts/enums/pdfViewer';
import { IProof } from 'src/ts/interfaces';
import AssetVerificationDocumentPanel from './components/AssetVerificationDocumentPanel/AssetVerificationDocumentPanel';
import { PdfViewerWrapper } from './components/AssetVerificationDocumentPanel/styles';
import { FlowLayout, FooterBar } from './styles';

type FlowContentProps = {
  entryId: string;
  proofs: IProof[];
  proofsReady: boolean;
};

const AssetVerificationFlowContent = ({ entryId, proofs, proofsReady }: FlowContentProps) => {
  const { state, isLoading, isError, storageReset, retry } = useHydrateReviewState(
    entryId,
    proofs,
    proofsReady
  );
  const { reviewItemId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The summary page's Previous button returns to the last document via ?doc=.
  const initialDoc = Number(searchParams.get('doc'));
  const [activeIndex, setActiveIndex] = useState(
    Number.isInteger(initialDoc) && initialDoc >= 0 ? initialDoc : 0
  );

  if (isLoading) return <Loader />;
  // Zero documents is a permanent condition, not a failed load: the entry has no
  // asset-eligible proofs and retrying returns the same nothing. It gets its own copy
  // and no action, because there is no action FP can offer. FVS rejects a zero-account
  // complete and FP does not call the release endpoint, so finishing this review needs
  // an FVS change. The signal in useHydrateReviewState is how we count these.
  if (!isError && state.documents.length === 0) {
    return (
      <FooterBar>
        <Text align={TextAlign.center} data-testid="asset-verification-no-documents">
          This entry has no asset documents to review.
        </Text>
      </FooterBar>
    );
  }
  if (isError) {
    return (
      <FooterBar>
        <Text align={TextAlign.center}>The review could not be loaded.</Text>
        <Button
          name="asset-verification-retry"
          data-testid="asset-verification-retry"
          size={ButtonSize.normal}
          color={ButtonColor.primary}
          variant={ButtonVariant.contained}
          onClick={() => retry()}
        >
          Retry
        </Button>
      </FooterBar>
    );
  }

  const documents = state.documents;
  // An out-of-range ?doc= (a stale summary link) clamps to the first document. Everything
  // below reads the clamped index, not the raw one: the counter and the Next/Review
  // choice are wrong if they disagree with the document actually on screen.
  const documentIndex = activeIndex < documents.length ? activeIndex : 0;
  const activeDocument = documents[documentIndex];

  // Bound by identity: FVS documentId IS the FP proof id. A miss is legitimate —
  // FVS may reference a proof this entry no longer lists — and falls through to
  // the no-preview placeholder below.
  const activeProof = proofForDocument(proofs, activeDocument.documentId);
  const isLastDocument = documentIndex === documents.length - 1;
  // Gated by validity (plan-review M3): Figma 23:4919 disables Next on an empty
  // panel, 23:6138 enables it once filled.
  const activeDocumentValid = validateDocument(state, activeDocument.documentId).length === 0;
  // Warning only: these statements are valid, so they never gate Next.
  const staleCodes = staleExcludingCodesForDocument(state, activeDocument.documentId);
  // The review-level 2023 banner is derived, not round-tripped: it hides once no
  // statement is stale (corrected or overridden), without waiting for recalculate.
  const staleStartLabel = reasonCodeLabel({ code: VOA_REASON_CODE_STALE_START });
  const reviewWarnings = (state.statusWarnings ?? []).filter(
    (warning) => warning !== staleStartLabel || hasStaleStartStatement(state.accounts)
  );

  return (
    <>
      {storageReset && (
        <div data-testid="voa-storage-reset-notice" role="status">
          <Text align={TextAlign.left}>
            An app update reset this review. Anything you had already entered needs to be entered
            again.
          </Text>
        </div>
      )}
      {reviewWarnings.length > 0 && (
        <div data-testid="review-status-warnings" role="status">
          {/* Keyed by index: labels are not guaranteed unique across codes. */}
          {reviewWarnings.map((warning, index) => (
            <Text key={`${index}-${warning}`} align={TextAlign.left}>
              {`\u26a0 ${warning}`}
            </Text>
          ))}
        </div>
      )}
      {staleCodes.length > 0 && (
        <div data-testid="review-stale-reason-codes" role="status">
          {staleCodes.map((stale) => (
            <Text key={`${stale.statementKey}-${stale.code}`} align={TextAlign.left}>
              {`\u26a0 ${stale.message}`}
            </Text>
          ))}
        </div>
      )}
      <FlowLayout>
        <PdfViewerWrapper>
          {activeProof?.file ? (
            <PDFViewer
              id={activeProof.id}
              file={activeProof.file}
              fileName={activeProof.fileName ?? ''}
              embedMode={EmbedMode.SIZED_CONTAINER}
              height="85vh"
              showZoomControl
            />
          ) : (
            <Text align={TextAlign.center}>No document preview available</Text>
          )}
        </PdfViewerWrapper>
        {/* Remount panel/form per document — only the active document mounts (decision 7). */}
        <AssetVerificationDocumentPanel
          key={activeDocument.documentId}
          entryId={entryId}
          documentId={activeDocument.documentId}
          documentIndex={activeDocument.index}
          docShortId={activeProof?.short_id ?? undefined}
        />
      </FlowLayout>
      <FooterBar>
        <Text align={TextAlign.right} data-testid="document-counter">
          Document {documentIndex + 1} of {documents.length} in this review
        </Text>
        {documentIndex > 0 && (
          <Button
            name="asset-verification-previous"
            data-testid="asset-verification-previous"
            size={ButtonSize.normal}
            variant={ButtonVariant.outline}
            onClick={() => setActiveIndex(Math.max(0, documentIndex - 1))}
          >
            Previous
          </Button>
        )}
        {isLastDocument ? (
          <Button
            name="asset-verification-review-calculation"
            data-testid="asset-verification-review-calculation"
            size={ButtonSize.normal}
            color={ButtonColor.primary}
            variant={ButtonVariant.contained}
            isDisabled={!activeDocumentValid}
            onClick={() =>
              navigate(`/my-work/asset-verification/${reviewItemId}/${entryId}/summary`)
            }
          >
            Review asset calculation
          </Button>
        ) : (
          <Button
            name="asset-verification-next"
            data-testid="asset-verification-next"
            size={ButtonSize.normal}
            color={ButtonColor.primary}
            variant={ButtonVariant.contained}
            isDisabled={!activeDocumentValid}
            onClick={() => setActiveIndex(Math.min(documents.length - 1, documentIndex + 1))}
          >
            Next document
          </Button>
        )}
      </FooterBar>
    </>
  );
};

const AssetVerificationFlowPage = () => {
  const { entryId } = useParams();
  const hasReviewFeature = useUserFeature('asset_verification_review');

  const {
    proofs,
    isUninitialized: isProofsUninitialized,
    isLoading: isLoadingProofs,
    isError: isProofsError
  } = useProofsFilteredByEntryId(entryId ?? '');
  const proofsReady = !isProofsUninitialized && !isLoadingProofs;

  if (!entryId) return <Loader />;
  if (!hasReviewFeature) {
    return (
      <Text align={TextAlign.center} data-testid="asset-verification-denied">
        You do not have access to asset verification reviews.
      </Text>
    );
  }
  // Mounting does not wait on the proofs fetch; see useHydrateReviewState.
  if (isProofsError) {
    return (
      <Text align={TextAlign.center} data-testid="asset-verification-proofs-error">
        The applicant&apos;s documents could not be loaded. Refresh and try again.
      </Text>
    );
  }

  return (
    // key: a client-side navigate between two VOA entries must remount the provider
    // subtree (including useHydrateReviewState's one-shot ref), or the previous
    // applicant's data would render under the new entryId until a hard reload.
    <AssetVerificationReviewProvider
      key={voaStorageKey(entryId)}
      storageKey={voaStorageKey(entryId)}
    >
      <AssetVerificationFlowContent entryId={entryId} proofs={proofs} proofsReady={proofsReady} />
    </AssetVerificationReviewProvider>
  );
};

export default AssetVerificationFlowPage;
